import { AuthenticationResult } from "@azure/msal-browser";
import { OfficeWrapper } from "../../services/OfficeWrapper";
import { ConfigurationService } from "../../services/ConfigurationService";
import { Logger } from "../../services/Logger";
import {
    ISharePointInteractiveAuthProvider,
    SharePointMsalClientOptions,
} from "../../services/SharePoint/SharePointAuthService";
import AsyncResult = Office.AsyncResult;
import Dialog = Office.Dialog;

/**
 * Implements the interactive step of the SharePoint MSAL flow via the Office Dialog API,
 * mirroring LoginDialog.tsx's pattern: the dialog's first URL must be same-origin (see
 * SharePointConsentInitiateComponent), which then runs MSAL's own redirect flow. The
 * hosted page posts the result back here via Office.context.ui.messageParent.
 *
 * One instance per SharePoint connection: the dialog page has no project context, so the
 * connection's clientId/authority (and the mailbox user's email as a sign-in hint) travel on the dialog URL.
 */
export default class SharePointConsentDialog implements ISharePointInteractiveAuthProvider {
    constructor(
        private office: OfficeWrapper,
        private configService: ConfigurationService,
        private logger: Logger,
        private msalOptions: SharePointMsalClientOptions,
        private loginHint?: string
    ) {}

    async acquireTokenInteractive(scopes: string[]): Promise<AuthenticationResult> {
        const query = new URLSearchParams({
            scopes: scopes.join(" "),
            clientId: this.msalOptions.clientId,
            authority: this.msalOptions.authority,
        });
        if (this.loginHint) {
            query.set("loginHint", this.loginHint);
        }
        const routingUrl = this.buildRoutingUrl(query);

        // Logs end up in support diagnostics; keep the URL but not the mailbox address.
        if (this.loginHint) {
            query.set("loginHint", "[REDACTED]");
        }
        this.logger.info(
            `SharePointConsentDialog [MSAL-only]: step 1/3 - Office.context.ui.displayDialogAsync(${this.buildRoutingUrl(
                query
            )}) - opening the Office Dialog that will run MSAL's acquireTokenRedirect`
        );

        return new Promise<AuthenticationResult>((resolve, reject) => {
            this.office.displayDialogAsync(
                routingUrl,
                { height: 60, width: 60, promptBeforeOpen: false },
                (asyncResult: AsyncResult<Dialog>) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                        this.logger.error(
                            "SharePointConsentDialog [MSAL-only]: displayDialogAsync failed",
                            asyncResult.error
                        );
                        reject(asyncResult.error);
                        return;
                    }

                    this.logger.info(
                        "SharePointConsentDialog [MSAL-only]: step 2/3 - dialog opened, waiting for " +
                            "SharePointConsentRedirectComponent's messageParent (posted once " +
                            "handleRedirectPromise resolves inside the dialog)"
                    );

                    const dialog = asyncResult.value;

                    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
                        try {
                            const payload = JSON.parse(arg.message);
                            if (!payload) {
                                reject(new Error("Ill-formatted or missing SharePoint consent message"));
                                return;
                            }
                            if (payload.error) {
                                this.logger.error(
                                    "SharePointConsentDialog [MSAL-only]: step 3/3 - dialog reported a consent/redirect error",
                                    payload.error
                                );
                                reject(new Error(payload.error));
                                return;
                            }
                            // Anything else posting to this parent (e.g. office-js-helpers' isAuthDialog echoing the
                            // return URL) must not be mistaken for a token.
                            if (typeof payload.accessToken !== "string" || !payload.accessToken) {
                                this.logger.error(
                                    "SharePointConsentDialog [MSAL-only]: step 3/3 - dialog posted a message without an access token"
                                );
                                reject(new Error("SharePoint consent dialog returned no access token"));
                                return;
                            }
                            this.logger.info(
                                "SharePointConsentDialog [MSAL-only]: step 3/3 - received the token result " +
                                    "from the dialog (user prompt or silent SSO on the Microsoft page), closing it"
                            );
                            resolve(payload as AuthenticationResult);
                        } catch (error) {
                            reject(error);
                        } finally {
                            dialog.close();
                        }
                    });

                    dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: any) => {
                        this.logger.warning(
                            `SharePointConsentDialog [MSAL-only]: dialog closed unexpectedly (user closed it or it failed to load)\n${JSON.stringify(
                                arg
                            )}`
                        );
                        reject(new Error(`SharePoint consent was not completed: ${JSON.stringify(arg)}`));
                    });
                }
            );
        });
    }

    private buildRoutingUrl(query: URLSearchParams): string {
        return `${this.configService.originURL}/index.html?${query.toString()}#sharepointconsentrouting`;
    }
}
